import { NotificationCentre } from './NotificationCentre';
import { PlayerAccessManager } from './PlayerAccessManager';
import { PlayerSettingsView } from './PlayerSettingsView';
import { useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2,
  Database,
  Download,
  Languages,
  RefreshCw,
  Server,
  ShieldCheck,
  Smartphone,
  Trash2,
  Users,
  Crown,
  MailPlus,
  UserCog,
  AlertTriangle,
  CalendarCheck2,
  CloudCog,
  ListChecks,
  HardDriveDownload,
  LockKeyhole,
  Stethoscope,
  Wifi,
  HardDrive,
  AppWindow,
  KeyRound,
  UserCheck,
} from 'lucide-react';
import { useData } from './DataContext';
import { useI18n } from './I18nContext';
import { clearPublicKey, getStoredPublicKey, getSupabase, supabaseProjectUrl } from './supabase';
import { CLUB_STAFF, canManageAccess, normalizeMembershipRole, isStaffRole } from './staffAccess';


const APP_VERSION = '24.0.0';
function downloadJson(filename: string, value: unknown) {
  const blob = new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

type DiagnosticResult = {
  id: string;
  label: string;
  ok: boolean;
  detail: string;
};

export function SettingsCentre() {
  const { language, setLanguage, t } = useI18n();
  const {
    team,
    teams,
    players,
    matches,
    relationships,
    lineups,
    leagueClubs,
    officialStandings,
    officialScorers,
    officialResults,
    officialLeagueSyncedAt,
    loading,
    error,
    refresh,
    setActiveTeam,
  } = useData();
  const [message, setMessage] = useState('');
  const [confirmReset, setConfirmReset] = useState(false);
  const [lastEmergencyBackup, setLastEmergencyBackup] = useState(() => {
    try { return localStorage.getItem('ecc_last_emergency_backup') || ''; } catch { return ''; }
  });
  const [diagnosticsRunning, setDiagnosticsRunning] = useState(false);
  const [diagnostics, setDiagnostics] = useState<DiagnosticResult[]>([]);
  const [lastDiagnosticsAt, setLastDiagnosticsAt] = useState('');
  const [sessionEmail, setSessionEmail] = useState('');

  useEffect(() => {
    let cancelled = false;
    const client = getSupabase();
    if (!client) return;
    void client.auth.getUser().then(({ data }) => {
      if (!cancelled) setSessionEmail(data.user?.email || '');
    });
    return () => { cancelled = true; };
  }, []);

  const membershipRole = normalizeMembershipRole(team?.role);
  const administrator = canManageAccess(team?.role);

  const matchdayLinks = useMemo(() => {
    try {
      return Object.keys(localStorage).filter((key) => key.startsWith('ecc_matchday_link_'));
    } catch {
      return [];
    }
  }, [lineups.length, matches.length]);

  const readinessChecks = useMemo(() => {
    const hasTeam = Boolean(team?.id);
    const hasRoster = players.length >= 11;
    const hasLineup = lineups.length > 0;
    const hasMatch = matches.length > 0;
    const leagueIdentityReady = leagueClubs.length === 11;
    const online = navigator.onLine;
    const cloudHealthy = !error;
    const officialSyncConfigured =
      officialStandings.length > 0 ||
      officialScorers.length > 0 ||
      officialResults.length > 0 ||
      Boolean(officialLeagueSyncedAt);
    const matchdayPrepared = matchdayLinks.length > 0;
    const lineupDraftProtected = team?.id
      ? Boolean(localStorage.getItem(`ecc_lineup_draft_${team.id}`))
      : false;

    return [
      { id: 'cloud', label: t.cloudConnection, ready: cloudHealthy && hasTeam },
      { id: 'online', label: t.onlineStatus, ready: online },
      { id: 'roster', label: t.squad, ready: hasRoster, detail: `${players.length}/11+` },
      { id: 'lineup', label: t.lineup, ready: hasLineup || lineupDraftProtected, detail: hasLineup ? `${lineups.length} cloud` : lineupDraftProtected ? t.localBackup : '0' },
      { id: 'match', label: t.matches, ready: hasMatch, detail: `${matches.length}` },
      { id: 'matchday', label: t.matchday, ready: matchdayPrepared, detail: `${matchdayLinks.length}` },
      { id: 'league', label: t.leagueIdentity, ready: leagueIdentityReady, detail: `${leagueClubs.length}/11` },
      {
        id: 'gdsfc',
        label: 'GDSFC Auto Sync',
        ready: officialSyncConfigured,
        detail: officialLeagueSyncedAt ? new Date(officialLeagueSyncedAt).toLocaleString() : t.waiting,
        advisory: !officialSyncConfigured,
      },
    ];
  }, [
    team,
    players.length,
    lineups.length,
    matches.length,
    leagueClubs.length,
    error,
    officialStandings.length,
    officialScorers.length,
    officialResults.length,
    officialLeagueSyncedAt,
    matchdayLinks.length,
    t,
  ]);

  const criticalChecks = readinessChecks.filter((item) => !item.advisory);
  const criticalReady = criticalChecks.filter((item) => item.ready).length;
  const systemReadiness = Math.round((criticalReady / criticalChecks.length) * 100);

  const connectionFingerprint = useMemo(() => {
    const key = getStoredPublicKey();
    if (!key) return '—';
    return `${key.slice(0, 8)}••••${key.slice(-6)}`;
  }, []);

  async function refreshData() {
    setMessage(t.refreshing);
    await refresh();
    setMessage(t.refreshComplete);
  }

  function exportBackup() {
    downloadJson(`elite-coach-cloud-backup-${new Date().toISOString().slice(0, 10)}.json`, {
      exported_at: new Date().toISOString(),
      team,
      teams,
      players,
      matches,
      relationships,
      lineups,
    });
    setMessage(t.backupCreated);
  }

  function collectEmergencyLocalState() {
    const localState: Record<string, unknown> = {};
    try {
      for (let index = 0; index < localStorage.length; index += 1) {
        const key = localStorage.key(index);
        if (!key || !key.startsWith('ecc_')) continue;
        const raw = localStorage.getItem(key);
        if (raw == null) continue;
        try {
          localState[key] = JSON.parse(raw);
        } catch {
          localState[key] = raw;
        }
      }
    } catch {
      // Device storage can be unavailable in restrictive/private modes.
    }
    return localState;
  }

  function exportEmergencySnapshot() {
    const exportedAt = new Date().toISOString();
    const localState = collectEmergencyLocalState();

    downloadJson(`elite-coach-cloud-matchday-snapshot-${exportedAt.replace(/[:.]/g, '-')}.json`, {
      schema: 'elite-coach-cloud-emergency-snapshot-v1',
      exported_at: exportedAt,
      application_version: APP_VERSION,
      device: {
        platform: navigator.platform || 'unknown',
        online: navigator.onLine,
        standalone: window.matchMedia('(display-mode: standalone)').matches,
      },
      cloud_data: {
        team,
        teams,
        players,
        matches,
        relationships,
        lineups,
        league_clubs: leagueClubs,
        official_standings: officialStandings,
        official_scorers: officialScorers,
        official_results: officialResults,
        official_league_synced_at: officialLeagueSyncedAt,
      },
      local_matchday_state: localState,
      local_key_count: Object.keys(localState).length,
    });

    try {
      localStorage.setItem('ecc_last_emergency_backup', exportedAt);
    } catch {
      // Export remains valid even if timestamp cannot be stored.
    }
    setLastEmergencyBackup(exportedAt);
    setMessage(t.emergencySnapshotCreated);
  }

  async function runReleaseDiagnostics() {
    setDiagnosticsRunning(true);
    setMessage('');

    const results: DiagnosticResult[] = [];

    // Local storage write/read/delete.
    try {
      const key = 'ecc_diagnostic_probe';
      localStorage.setItem(key, 'ok');
      const ok = localStorage.getItem(key) === 'ok';
      localStorage.removeItem(key);
      results.push({
        id: 'storage',
        label: t.localStorageDiagnostic,
        ok,
        detail: ok ? t.operational : t.attentionRequired,
      });
    } catch {
      results.push({
        id: 'storage',
        label: t.localStorageDiagnostic,
        ok: false,
        detail: t.storageUnavailable,
      });
    }

    results.push({
      id: 'network',
      label: t.networkDiagnostic,
      ok: navigator.onLine,
      detail: navigator.onLine ? t.online : t.offline,
    });

    const standalone = window.matchMedia('(display-mode: standalone)').matches;
    results.push({
      id: 'pwa',
      label: t.pwaDiagnostic,
      ok: true,
      detail: standalone ? 'PWA / Standalone' : t.browser,
    });

    results.push({
      id: 'roster',
      label: t.rosterDiagnostic,
      ok: players.length >= 11,
      detail: `${players.length} ${t.players}`,
    });

    const protectedLineup = (() => {
      try {
        return Boolean(team?.id && localStorage.getItem(`ecc_lineup_draft_${team.id}`));
      } catch {
        return false;
      }
    })();

    results.push({
      id: 'lineup',
      label: t.lineupDiagnostic,
      ok: lineups.length > 0 || protectedLineup,
      detail: lineups.length > 0
        ? `${lineups.length} cloud`
        : protectedLineup
          ? t.localBackupActive
          : t.noSavedLineups,
    });

    results.push({
      id: 'matchday',
      label: t.matchdayDiagnostic,
      ok: matches.length > 0 && matchdayLinks.length > 0,
      detail: `${matches.length} ${t.matches} · ${matchdayLinks.length} ${t.matchday}`,
    });

    results.push({
      id: 'league',
      label: t.leagueDiagnostic,
      ok: leagueClubs.length === 11,
      detail: `${leagueClubs.length}/11`,
    });

    const officialFeedPresent =
      officialStandings.length > 0 ||
      officialScorers.length > 0 ||
      officialResults.length > 0 ||
      Boolean(officialLeagueSyncedAt);

    results.push({
      id: 'gdsfc',
      label: 'GDSFC Auto Sync',
      ok: true,
      detail: officialFeedPresent
        ? (officialLeagueSyncedAt
            ? `${t.lastCloudSave}: ${new Date(officialLeagueSyncedAt).toLocaleString()}`
            : t.operational)
        : t.gdsfcSeasonGuardPending,
    });

    // Supabase lightweight read.
    try {
      const client = getSupabase();
      if (!client) {
        results.push({
          id: 'supabase',
          label: t.supabaseDiagnostic,
          ok: false,
          detail: t.connectionNotConfigured,
        });
      } else {
        const query = await client.from('teams').select('id').limit(1);
        results.push({
          id: 'supabase',
          label: t.supabaseDiagnostic,
          ok: !query.error,
          detail: query.error ? query.error.message : t.connected,
        });
      }
    } catch (error) {
      results.push({
        id: 'supabase',
        label: t.supabaseDiagnostic,
        ok: false,
        detail: error instanceof Error ? error.message : t.attentionRequired,
      });
    }

    setDiagnostics(results);
    const completedAt = new Date().toISOString();
    setLastDiagnosticsAt(completedAt);
    setDiagnosticsRunning(false);
  }

  function resetConnection() {
    if (!administrator) {
      setMessage(t.administratorRequired);
      setConfirmReset(false);
      return;
    }
    clearPublicKey();
    window.location.reload();
  }

  if (membershipRole === 'player' || isStaffRole(team?.role)) {
    const staffView = isStaffRole(team?.role);
    return (
      <PlayerSettingsView
        language={language}
        setLanguage={setLanguage}
        email={sessionEmail}
        teamName={team?.name || team?.club_name || 'Polonia Glasgow AFC'}
        accessLabel={staffView ? 'Staff · Matchday operations' : 'Player · Read only'}
        eyebrowRole={staffView ? 'STAFF' : 'PLAYER'}
      />
    );
  }

  return (
    <div className="settings-page">
      <section className="metal-card settings-hero">
        <div>
          <p className="eyebrow">{t.systemAdministration}</p>
          <h1>{t.settings}</h1>
          <p className="muted">{t.settingsSubtitle}</p>
        </div>
        <div className="system-health">
          <CheckCircle2 size={28} />
          <strong>{error || systemReadiness < 85 ? t.attentionRequired : t.operational}</strong>
          <span>V{APP_VERSION}</span>
        </div>
      </section>

      <section className="metal-card release-readiness-panel">
        <div className="release-readiness-header">
          <div>
            <p className="eyebrow">RELEASE CANDIDATE</p>
            <h2>{t.preMatchSystemCheck}</h2>
            <p className="muted">{t.preMatchSystemCheckDescription}</p>
          </div>
          <div className={`release-readiness-score ${systemReadiness >= 85 ? 'ready' : 'attention'}`}>
            <strong>{systemReadiness}</strong>
            <span>/100</span>
          </div>
        </div>

        <div className="release-readiness-bar"><i style={{ width: `${systemReadiness}%` }} /></div>

        <div className="release-check-grid">
          {readinessChecks.map((check) => (
            <div className={`release-check ${check.ready ? 'ready' : check.advisory ? 'advisory' : 'pending'}`} key={check.id}>
              {check.ready ? <CheckCircle2 size={17} /> : check.advisory ? <CloudCog size={17} /> : <AlertTriangle size={17} />}
              <div>
                <strong>{check.label}</strong>
                {check.detail && <span>{check.detail}</span>}
              </div>
            </div>
          ))}
        </div>

        <div className="release-check-actions">
          <button className="gold-button" type="button" onClick={() => void refreshData()} disabled={loading}>
            <RefreshCw size={16} /> {t.runSystemCheck}
          </button>
          <span><ListChecks size={15} /> {criticalReady}/{criticalChecks.length} {t.criticalChecksReady}</span>
        </div>
      </section>

      <section className="metal-card diagnostics-panel">
        <div className="diagnostics-header">
          <div>
            <p className="eyebrow">RELEASE DIAGNOSTICS</p>
            <h2>{t.deviceMatchdayDiagnostics}</h2>
            <p className="muted">{t.deviceMatchdayDiagnosticsDescription}</p>
          </div>
          <Stethoscope size={28} />
        </div>

        <div className="diagnostics-actions">
          <button
            className="gold-button"
            type="button"
            onClick={() => void runReleaseDiagnostics()}
            disabled={diagnosticsRunning}
          >
            <RefreshCw size={16} />
            {diagnosticsRunning ? t.runningDiagnostics : t.runDiagnostics}
          </button>
          {lastDiagnosticsAt && (
            <span>{new Date(lastDiagnosticsAt).toLocaleString()}</span>
          )}
        </div>

        {diagnostics.length > 0 && (
          <div className="diagnostics-grid">
            {diagnostics.map((item) => (
              <article className={item.ok ? 'diagnostic-item ok' : 'diagnostic-item fail'} key={item.id}>
                <span className="diagnostic-icon">
                  {item.id === 'network' ? <Wifi size={17} /> :
                   item.id === 'storage' ? <HardDrive size={17} /> :
                   item.id === 'pwa' ? <AppWindow size={17} /> :
                   item.ok ? <CheckCircle2 size={17} /> : <AlertTriangle size={17} />}
                </span>
                <div>
                  <strong>{item.label}</strong>
                  <small>{item.detail}</small>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="settings-grid">
        <article className="metal-card settings-card">
          <div className="settings-card-title">
            <Languages size={23} />
            <div><h2>{t.language}</h2><p>{t.languageDescription}</p></div>
          </div>
          <div className="segmented-control">
            <button className={language === 'en' ? 'active' : ''} onClick={() => setLanguage('en')}>
              English
            </button>
            <button className={language === 'pl' ? 'active' : ''} onClick={() => setLanguage('pl')}>
              Polski
            </button>
          </div>
        </article>

        <article className="metal-card settings-card">
          <div className="settings-card-title">
            <Users size={23} />
            <div><h2>{t.activeTeam}</h2><p>{t.activeTeamDescription}</p></div>
          </div>
          <select
            className="settings-select"
            value={team?.id ?? ''}
            onChange={(event) => void setActiveTeam(event.target.value)}
          >
            {teams.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.name || candidate.club_name || 'Team'}
              </option>
            ))}
          </select>
          <dl className="system-facts">
            <dt>{t.role}</dt><dd className="administrator-role-label">
              {membershipRole === 'administrator' ? <Crown size={14} /> : <Users size={14} />}
              {membershipRole === 'administrator' ? t.administrator : t.headCoach}
            </dd>
            <dt>{t.season}</dt><dd>{team?.season || '2026/27'}</dd>
            <dt>{t.players}</dt><dd>{players.length}</dd>
          </dl>
        </article>

        <article className="metal-card settings-card access-control-card">
          <div className="settings-card-title">
            <KeyRound size={23} />
            <div>
              <h2>{t.accessControl}</h2>
              <p>{t.accessControlDescription}</p>
            </div>
          </div>
          <dl className="system-facts">
            <dt>{t.signedInAccount}</dt><dd>{sessionEmail || '—'}</dd>
            <dt>{t.membershipRole}</dt>
            <dd className={administrator ? 'status-good' : ''}>
              {membershipRole === 'administrator' ? t.administrator : membershipRole === 'assistant_coach' ? 'Assistant Coach' : membershipRole === 'player' ? t.player : t.headCoach}
            </dd>
            <dt>{t.accessScope}</dt>
            <dd>{administrator ? t.fullAdministrativeAccess : t.coachingAccess}</dd>
          </dl>
          <div className="access-permission-grid">
            <span className="allowed"><UserCheck size={15} /> {t.players}</span>
            <span className="allowed"><UserCheck size={15} /> {t.lineup}</span>
            <span className="allowed"><UserCheck size={15} /> {t.matchday}</span>
            <span className="allowed"><UserCheck size={15} /> {t.training}</span>
            <span className="allowed"><UserCheck size={15} /> {t.analysis}</span>
            <span className="allowed"><UserCheck size={15} /> {t.league}</span>
            <span className={administrator ? 'allowed' : 'restricted'}>
              {administrator ? <UserCheck size={15} /> : <LockKeyhole size={15} />}
              {t.accessAdministration}
            </span>
          </div>
        </article>

        <article className="metal-card settings-card staff-access-card">
          <div className="settings-card-title">
            <UserCog size={23} />
            <div>
              <h2>{t.staffAccess}</h2>
              <p>{administrator ? t.staffAccessDescription : t.staffAccessReadOnlyDescription}</p>
            </div>
          </div>

          <div className="staff-access-list">
            {CLUB_STAFF.map((staff) => (
              <div className={`staff-access-row ${staff.role}`} key={staff.name}>
                <span className="staff-role-icon">
                  {staff.role === 'administrator' ? <Crown size={18} /> : <Users size={18} />}
                </span>
                <div>
                  <strong>{staff.name}</strong>
                  <span>
                    {staff.role === 'administrator' ? t.administrator : t.headCoach}
                    {staff.linkedPlayerName ? ` · ${t.player}` : ''}
                  </span>
                </div>
                <span className={`staff-status ${staff.invitationStatus}`}>
                  {staff.invitationStatus === 'active' ? t.active : t.invitationPending}
                </span>
              </div>
            ))}
          </div>

          {administrator ? (
            <div className="staff-invite-summary">
              <MailPlus size={17} />
              <div>
                <strong>2 {t.preparedInvitations}</strong>
                <span>{t.invitationEmailNeeded} · {t.invitesRemainPendingUntilAccountsLinked}</span>
              </div>
            </div>
          ) : (
            <div className="staff-admin-locked">
              <LockKeyhole size={17} />
              <div>
                <strong>{t.administratorOnly}</strong>
                <span>{t.staffAccessLockedDescription}</span>
              </div>
            </div>
          )}
        </article>

        {administrator && team?.id && (
          <PlayerAccessManager teamId={team.id} players={players} />
        )}

        <NotificationCentre />

        <article className="metal-card settings-card">
          <div className="settings-card-title">
            <Database size={23} />
            <div><h2>{t.cloudConnection}</h2><p>{t.cloudConnectionDescription}</p></div>
          </div>
          <dl className="system-facts">
            <dt>{t.project}</dt><dd>{supabaseProjectUrl.replace('https://', '')}</dd>
            <dt>{t.keyFingerprint}</dt><dd>{connectionFingerprint}</dd>
            <dt>{t.status}</dt><dd className={error ? 'status-warning' : 'status-good'}>
              {error ? t.attentionRequired : t.connected}
            </dd>
            <dt>{t.leagueIdentity}</dt><dd className={leagueClubs.length === 11 ? 'status-good' : 'status-warning'}>
              {leagueClubs.length}/11 {t.clubsLinked}
            </dd>
          </dl>
        </article>

        <article className="metal-card settings-card">
          <div className="settings-card-title">
            <Server size={23} />
            <div><h2>{t.dataInventory}</h2><p>{t.dataInventoryDescription}</p></div>
          </div>
          <div className="inventory-grid">
            <div><strong>{players.length}</strong><span>{t.players}</span></div>
            <div><strong>{matches.length}</strong><span>{t.matchesPlayed}</span></div>
            <div><strong>{relationships.length}</strong><span>{t.relationships}</span></div>
            <div><strong>{lineups.length}</strong><span>{t.savedLineups}</span></div>
          </div>
        </article>

        <article className="metal-card settings-card">
          <div className="settings-card-title">
            <Smartphone size={23} />
            <div><h2>{t.device}</h2><p>{t.deviceDescription}</p></div>
          </div>
          <dl className="system-facts">
            <dt>{t.platform}</dt><dd>{navigator.platform || t.unknown}</dd>
            <dt>{t.onlineStatus}</dt><dd>{navigator.onLine ? t.online : t.offline}</dd>
            <dt>{t.appMode}</dt><dd>{window.matchMedia('(display-mode: standalone)').matches ? 'PWA' : t.browser}</dd>
          </dl>
        </article>

        <article className="metal-card settings-card action-card">
          <div className="settings-card-title">
            <ShieldCheck size={23} />
            <div><h2>{t.maintenance}</h2><p>{t.maintenanceDescription}</p></div>
          </div>
          <div className="settings-actions">
            <button className="steel-button" onClick={() => void refreshData()} disabled={loading}>
              <RefreshCw size={17} /> {t.refreshData}
            </button>
            <button className="steel-button" onClick={exportBackup}>
              <Download size={17} /> {t.exportBackup}
            </button>
          </div>
          {message && <p className="settings-message">{message}</p>}
        </article>

        <article className="metal-card settings-card emergency-backup-card">
          <div className="settings-card-title">
            <HardDriveDownload size={23} />
            <div>
              <h2>{t.matchdayEmergencyBackup}</h2>
              <p>{t.matchdayEmergencyBackupDescription}</p>
            </div>
          </div>

          <div className="emergency-backup-facts">
            <span><LockKeyhole size={15} /> {t.includesCloudAndLocalData}</span>
            <span>
              {t.lastBackup}: {lastEmergencyBackup ? new Date(lastEmergencyBackup).toLocaleString() : t.never}
            </span>
          </div>

          <button className="emergency-backup-button" type="button" onClick={exportEmergencySnapshot}>
            <Download size={17} /> {t.createMatchdaySnapshot}
          </button>
        </article>

        <article className={`metal-card settings-card danger-card ${administrator ? '' : 'admin-locked-card'}`}>
          <div className="settings-card-title">
            <Trash2 size={23} />
            <div>
              <h2>{t.resetConnection}</h2>
              <p>{administrator ? t.resetConnectionDescription : t.administratorRequired}</p>
            </div>
          </div>
          {administrator ? (
            !confirmReset ? (
              <button className="danger-button" onClick={() => setConfirmReset(true)}>
                {t.prepareReset}
              </button>
            ) : (
              <div className="reset-confirmation">
                <p>{t.resetWarning}</p>
                <button className="danger-button" onClick={resetConnection}>{t.confirmReset}</button>
                <button className="steel-button" onClick={() => setConfirmReset(false)}>{t.cancel}</button>
              </div>
            )
          ) : (
            <div className="staff-admin-locked">
              <LockKeyhole size={17} />
              <div>
                <strong>{t.administratorOnly}</strong>
                <span>{t.accessAdministrationLocked}</span>
              </div>
            </div>
          )}
        </article>
      </section>
    </div>
  );
}
